<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    :title="title"
    :subtitle="subtitle"
    :icon="ICONS.actions.levelUp"
    closeLabel="Close level-up"
    :style="{ '--accent': 'var(--accent-work)' }"
    wide
    @close="emit('close')"
  >
    <div class="stack tight">
      <UiPanel tone="work">
        <UiSectionHeader>
          <template #title>Summary</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">Lv {{ formatNumber(newLevel) }}</UiChip>
          </template>
        </UiSectionHeader>

        <div class="kv-grid">
          <div class="kv">
            <div class="kv-label">Character</div>
            <div class="kv-value">{{ characterName }}</div>
          </div>
          <div class="kv">
            <div class="kv-label">AP awarded</div>
            <div class="kv-value stat-mono num">{{ formatNumber(apAwarded) }}</div>
          </div>
          <div class="kv">
            <div class="kv-label">AP total</div>
            <div class="kv-value stat-mono num">{{ formatNumber(totalAp) }}</div>
          </div>
        </div>
      </UiPanel>

      <UiPanel tone="work">
        <UiSectionHeader>
          <template #title>XP</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(currentXp) }} / {{ formatNumber(nextLevelXp) }}</UiChip>
          </template>
        </UiSectionHeader>
        <UiProgressBar :value="xpPct" tone="work" />
      </UiPanel>

      <UiCallout v-if="remainingLevelups > 0" variant="info">
        {{ formatNumber(remainingLevelups) }} more level-up(s) queued after this one.
      </UiCallout>

      <div class="row wrap" style="justify-content: flex-end;">
        <UiButton v-if="totalAp > 0" size="sm" variant="secondary" @click="handleSpendAp">
          <template #icon>{{ ICONS.actions.spendAp }}</template>
          Spend AP
        </UiButton>
        <UiButton size="sm" tone="work" @click="handleContinue">
          <template #icon>{{ ICONS.states.active }}</template>
          Confirm Level Up
        </UiButton>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    characterName: string;
    newLevel: number;
    currentXp: number;
    nextLevelXp: number;
    apAwarded: number;
    totalAp: number;
    remainingLevelups: number;
    onContinue?: () => Promise<void> | void;
    onSpendAp?: () => Promise<void> | void;
  }>(),
  {
    overlayZIndex: 4000,
    onContinue: undefined,
    onSpendAp: undefined
  }
);

const emit = defineEmits<{ close: [] }>();

const title = computed(() => "Level Up");
const subtitle = computed(() => `Confirm level ${formatNumber(props.newLevel)} for ${props.characterName}.`);
const xpPct = computed(() => {
  const denom = Math.max(1, Math.floor(Number(props.nextLevelXp || 1)));
  const num = Math.max(0, Math.floor(Number(props.currentXp || 0)));
  return Math.max(0, Math.min(100, Math.round((num / denom) * 100)));
});

async function handleContinue() {
  await props.onContinue?.();
  emit("close");
}

async function handleSpendAp() {
  await props.onSpendAp?.();
  emit("close");
}
</script>

<style scoped>
.kv-grid {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
}

.kv {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.6rem 0.65rem;
}

.kv-label {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  margin-top: 0.2rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 760px) {
  .kv-grid {
    grid-template-columns: 1fr;
  }
}
</style>
